import { useEffect } from "react";

import { registerDialogSurface } from "../lib/escapeConsumption";

/**
 * Declare a node as an open modal surface for as long as `active` holds.
 *
 * One line, because the register is only useful if it is exhaustive for the
 * Facilitator OS (see the scope note in `escapeConsumption.js`) and a
 * three-line effect at every call site is how a surface comes to be missed.
 *
 * `active` is a parameter rather than an assumption because two OS surfaces are
 * dialogs only sometimes: Review's workspace is `role="dialog"` at narrow
 * widths and a plain column otherwise, and `CommitOutcome` is a dialog only
 * while it holds a one-shot credential. Both must leave the register the moment
 * they stop being modal, or an Assistant gesture stands down for a panel that
 * is no longer in anybody's way.
 *
 * `layer` is the surface's CSS stacking tier and is forwarded to the register:
 * the assistant passes its z-[90], everything on the shared `Dialog` frame
 * stays on the default. It exists for the one surface that can JOIN the stack
 * under an already-open assistant (Review's workspace, mounted by a viewport
 * flip); see `registerDialogSurface`'s docblock.
 *
 * @param {{ current: Element|null }} ref the panel node
 * @param {boolean} [active=true]
 * @param {number} [layer=0]
 */
export const useDialogSurface = (ref, active = true, layer = 0) => {
  useEffect(() => {
    if (!active) return undefined;
    return registerDialogSurface(ref.current, layer);
    // `ref` is a stable ref object; `ref.current` is read at effect time, after
    // the commit that attached it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, layer]);
};

export default useDialogSurface;
